import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Grid, MenuItem, TextField, Button, CircularProgress,
  Alert, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Divider, Checkbox, FormControlLabel,
  Avatar, Stack, Collapse,
} from '@mui/material';
import { People, HowToReg, ExpandMore, ExpandLess, PersonAdd } from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../../api/apiClient';

// ── Shared design tokens ───────────────────────────────────────────────────
const BG        = '#f5f8f6';
const SURFACE   = '#ffffff';
const BORDER    = '#d0ddd7';
const ACCENT    = '#2e7d5e';
const ACCENT_BG = '#e8f5ee';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';
// ──────────────────────────────────────────────────────────────────────────

/* ─── helpers ────────────────────────────────────────────────────── */
const initials = (p) =>
  `${p.firstName?.[0] ?? ''}${p.lastName?.[0] ?? ''}`.toUpperCase();

const age = (birthday) => {
  if (!birthday) return '—';
  const diff = Date.now() - new Date(birthday).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
};


// hook — put this outside your component (or in a separate file)
function useOnlineStatus() {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return isOnline;
}


/* ─── PlayerRow ──────────────────────────────────────────────────── */
const PlayerRow = ({ player, checked, onChange }) => (
  <TableRow
    hover
    sx={{
      cursor: 'pointer',
      bgcolor: checked ? ACCENT_BG : SURFACE,
      transition: 'background 0.15s',
      '&:hover': { bgcolor: checked ? ACCENT_BG : BG },
      '& td': { borderColor: BORDER },
    }}
    onClick={() => onChange(player._id)}
  >
    <TableCell padding="checkbox">
      <Checkbox
        checked={checked}
        onChange={() => onChange(player._id)}
        size="small"
        sx={{
          color: BORDER,
          '&.Mui-checked': { color: ACCENT },
        }}
      />
    </TableCell>
    <TableCell>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar
          sx={{
            width: 30, height: 30,
            fontSize: 11, fontWeight: 700,
            bgcolor: ACCENT_BG,
            color: ACCENT,
            border: `1px solid ${BORDER}`,
          }}
        >
          {initials(player)}
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ color: TEXT_PRI, fontSize: 13 }}>
            {player.firstName} {player.lastName}
          </Typography>
          <Typography variant="caption" sx={{ color: TEXT_SEC }}>
            {player.nationality}
          </Typography>
        </Box>
      </Stack>
    </TableCell>
    <TableCell>
      <Box
        sx={{
          display: 'inline-flex', alignItems: 'center',
          px: 1, py: 0.25,
          borderRadius: '6px',
          border: `1px solid ${BORDER}`,
          bgcolor: BG,
          color: TEXT_SEC,
          fontSize: 12, fontWeight: 500,
        }}
      >
        #{player.actualJerseyNumber ?? '?'}
      </Box>
    </TableCell>
    <TableCell>
      <Box
        sx={{
          display: 'inline-flex', alignItems: 'center',
          px: 1, py: 0.25,
          borderRadius: '6px',
          border: `1px solid ${ACCENT}55`,
          bgcolor: ACCENT_BG,
          color: ACCENT,
          fontSize: 12, fontWeight: 500,
        }}
      >
        {player.positionOnField ?? '—'}
      </Box>
    </TableCell>
    <TableCell>
      <Typography variant="body2" sx={{ color: TEXT_PRI, fontSize: 13 }}>
        {age(player.birthday)} ans
      </Typography>
    </TableCell>
    <TableCell>
      <Box
        sx={{
          display: 'inline-flex', alignItems: 'center',
          px: 1, py: 0.25,
          borderRadius: '6px',
          border: `1px solid ${BORDER}`,
          bgcolor: BG,
          color: TEXT_SEC,
          fontSize: 12,
          textTransform: 'capitalize',
        }}
      >
        {player.preferredFoot ?? '—'}
      </Box>
    </TableCell>
  </TableRow>
);

/* ─── EntitySection ──────────────────────────────────────────────── */
const EntitySection = ({ entityId, label, selectedIds, onToggle, onPlayersLoaded }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [open, setOpen]       = useState(true);



  const fetchPlayers = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiCall((headers) =>
        axios.get(`${BASE_URL}/entities/${entityId}/players`, { headers })
      );
      const list = res.data.data?.players ?? [];
      setPlayers(list);
      onPlayersLoaded(entityId, list);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  const allChecked = players.length > 0 && players.every((p) => selectedIds.includes(p._id));
  const someChecked = selectedIds.length > 0 && !allChecked;

  const toggleAll = () => {
    players.forEach((p) => {
      const checked = selectedIds.includes(p._id);
      if (allChecked ? checked : !checked) onToggle(entityId, p._id);
    });
  };

  

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        overflow: 'hidden',
        mb: 2,
      }}
    >
      {/* header */}
      <Box
        sx={{
          px: 2, py: 1.2,
          bgcolor: BG,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
          borderBottom: open ? `1px solid ${BORDER}` : 'none',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <People fontSize="small" sx={{ color: ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: TEXT_PRI }}>
            {label}
          </Typography>
          {!loading && (
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center',
                px: 1, py: 0.2,
                borderRadius: '6px',
                border: `1px solid ${ACCENT}55`,
                bgcolor: ACCENT_BG,
                color: ACCENT,
                fontSize: 11, fontWeight: 600,
              }}
            >
              {players.length} joueurs
            </Box>
          )}
          {loading && <CircularProgress size={14} sx={{ color: ACCENT }} />}
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={(e) => { e.stopPropagation(); toggleAll(); }}
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: BORDER,
                  '&.Mui-checked': { color: ACCENT },
                  '&.MuiCheckbox-indeterminate': { color: ACCENT },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: TEXT_SEC, fontSize: 12 }}>
                Tout sélectionner
              </Typography>
            }
            onClick={(e) => e.stopPropagation()}
          />
          {open
            ? <ExpandLess fontSize="small" sx={{ color: TEXT_SEC }} />
            : <ExpandMore fontSize="small" sx={{ color: TEXT_SEC }} />
          }
        </Box>
      </Box>

      <Collapse in={open}>
        {error && (
          <Alert
            severity="error"
            sx={{
              m: 1, borderRadius: '10px',
              bgcolor: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca',
              '& .MuiAlert-icon': { color: '#dc2626' },
            }}
          >
            {error}
          </Alert>
        )}
        {!loading && players.length === 0 && !error && (
          <Typography variant="body2" sx={{ color: TEXT_SEC, p: 2 }}>
            Aucun joueur trouvé pour cette entité.
          </Typography>
        )}
        {players.length > 0 && (
          <TableContainer sx={{ bgcolor: SURFACE }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: BG }}>
                  <TableCell padding="checkbox" />
                  {['Joueur', 'Maillot', 'Poste', 'Âge', 'Pied'].map((h) => (
                    <TableCell key={h}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: TEXT_SEC,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          fontSize: 11,
                        }}
                      >
                        {h}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {players.map((p) => (
                  <PlayerRow
                    key={p._id}
                    player={p}
                    checked={selectedIds.includes(p._id)}
                    onChange={(id) => onToggle(entityId, id)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Box>
  );
};

/* ─── RegisteredTable ────────────────────────────────────────────── */
const RegisteredTable = ({ game }) => {
  const registrations = game?.players ?? [];
  if (registrations.length === 0) return null;

  const totalCount = registrations.reduce((acc, r) => acc + (r.userIds?.length ?? 0), 0);

 return (
  <>
    <Divider sx={{ my: 3, borderColor: '#E5E7EB' }} />
    <Typography
      variant="subtitle2"
      gutterBottom
      sx={{ fontWeight: 600, color: '#111827', mb: 1.5, fontSize: 13, letterSpacing: '0.01em' }}
    >
      Joueurs inscrits ({totalCount})
    </Typography>

    {registrations.map((reg) => (
      <Box key={reg.entityId?._id ?? reg.entityId} sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: '#6366F1', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}
        >
          {reg.entityId?.name ?? reg.entityId}
        </Typography>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #E5E7EB',
            borderRadius: '14px',
            mt: 1,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                {['Joueur', 'Maillot', 'Poste'].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ borderBottom: '1px solid #E5E7EB', py: 1.25, px: 2 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#9CA3AF',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.07em',
                        fontSize: 10,
                      }}
                    >
                      {h}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(reg.userIds ?? []).map((u) => {
                const player = typeof u === 'object' ? u : null;
                return (
                  <TableRow
                    key={player?._id ?? u}
                    sx={{
                      '&:hover': { bgcolor: '#F9FAFB' },
                      '& td': { borderColor: '#F3F4F6' },
                      transition: 'background 0.15s',
                    }}
                  >
                    <TableCell sx={{ px: 2, py: 1.25 }}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Avatar
                          sx={{
                            width: 28, height: 28,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: '#EEF2FF',
                            color: '#6366F1',
                            border: '1px solid #E0E7FF',
                          }}
                        >
                          {player ? initials(player) : '?'}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{ color: '#111827', fontSize: 13, fontWeight: 500 }}
                        >
                          {player ? `${player.firstName} ${player.lastName}` : u}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ px: 2, py: 1.25 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.25,
                          py: 0.4,
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          bgcolor: '#F9FAFB',
                          color: '#374151',
                          fontSize: 12,
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                        }}
                      >
                        #{player?.actualJerseyNumber ?? '?'}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ px: 2, py: 1.25 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: 1.25,
                          py: 0.4,
                          borderRadius: '8px',
                          border: '1px solid #E0E7FF',
                          bgcolor: '#EEF2FF',
                          color: '#6366F1',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {player?.positionOnField ?? '—'}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    ))}
  </>
);
};

/* ─── PlayersTab (main) ──────────────────────────────────────────── */
const PlayersTab = ({ game, onRefresh }) => {
  const [selectedMap, setSelectedMap]               = useState({});
  const [playersMap, setPlayersMap]                 = useState({});
  const [loading, setLoading]                       = useState(false);
  const [error, setError]                           = useState('');
  const [success, setSuccess]                       = useState('');
  const [canRegisterPlayers, setCanRegisterPlayers] = useState(false);

  // ── Permission check ───────────────────────────────────────────────────────
  // useEffect(() => {
  //   const currentUser = (() => {
  //     try {
  //       const user = localStorage.getItem('user');
  //       if (user) return JSON.parse(user);
  //     } catch {}
  //     return null;
  //   })();

  //   if (!currentUser) return;

  //   if (currentUser.role === 'entityPlatformAdmin') {
  //     setCanRegisterPlayers(true);
  //     return;
  //   }

  //   const ownerEntry = (game?.eventOwnersAndRoles || []).find(
  //     (entry) => entry.userId?._id === currentUser._id
  //   );
  //   if (!ownerEntry) return;

  //   const roles = ownerEntry.roles || [];
  //   setCanRegisterPlayers(
  //     roles.includes('eventSuperAdmin') || roles.includes('eventGameUpdater')
  //   );
  // }, [game?._id]);


  // ── Permission check ───────────────────────────────────────────────────────
useEffect(() => {
  const currentUser = (() => {
    try {
      const user = localStorage.getItem('user');
      if (user) return JSON.parse(user);
    } catch {}
    return null;
  })();

  if (!currentUser) return;

  const ownerEntry = (game?.eventOwnersAndRoles || []).find(
    (entry) => entry.userId?._id === currentUser._id
  );
  if (!ownerEntry) return;

  setCanRegisterPlayers(true);
}, [game?._id]);


  // ──────────────────────────────────────────────────────────────────────────
  const isOnline = useOnlineStatus();

  const entity1Id   = game?.entity1?._id;
  const entity2Id   = game?.entity2?._id;
  const entity1Name = game?.entity1?.name ?? 'Team 1';
  const entity2Name = game?.entity2?.name ?? 'Team 2';

  const handleToggle = (entityId, userId) => {
    setSelectedMap((prev) => {
      const current = new Set(prev[entityId] ?? []);
      current.has(userId) ? current.delete(userId) : current.add(userId);
      return { ...prev, [entityId]: current };
    });
  };

  const handlePlayersLoaded = (entityId, players) => {
    setPlayersMap((prev) => ({ ...prev, [entityId]: players }));
  };

  const handleSubmit = async () => {
    const payload = [entity1Id, entity2Id]
      .filter(Boolean)
      .map((eId) => ({
        entityId: eId,
        userIds: [...(selectedMap[eId] ?? [])],
      }))
      .filter((entry) => entry.userIds.length > 0);

    if (payload.length === 0) {
      setError('Veuillez sélectionner au moins un joueur.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await apiCall((headers) =>
        axios.post(`${BASE_URL}/games/${game._id}/players`, payload, { headers })
      );
      setSuccess('Joueurs inscrits avec succès !');
      setSelectedMap({});
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register players');
    } finally {
      setLoading(false);
    }
  };

  const totalSelected = Object.values(selectedMap).reduce(
    (acc, set) => acc + (set?.size ?? 0),
    0
  );

  return (
    <Box sx={{ color: TEXT_PRI }}>
      {/* ── Registration form ── */}
      {canRegisterPlayers && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEXT_PRI }}>
              Inscrire des joueurs
            </Typography>
            {totalSelected > 0 && (
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 1.25, py: 0.4,
                  borderRadius: '8px',
                  border: `1px solid ${ACCENT}55`,
                  bgcolor: ACCENT_BG,
                  color: ACCENT,
                  fontSize: 12, fontWeight: 600,
                }}
              >
                <PersonAdd sx={{ fontSize: 14 }} />
                {totalSelected} sélectionné(s)
              </Box>
            )}
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2, borderRadius: '10px',
                bgcolor: '#fef2f2', color: '#991b1b',
                border: '1px solid #fecaca',
                '& .MuiAlert-icon': { color: '#dc2626' },
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {entity1Id && (
            <EntitySection
              entityId={entity1Id}
              label={entity1Name}
              selectedIds={[...(selectedMap[entity1Id] ?? [])]}
              onToggle={handleToggle}
              onPlayersLoaded={handlePlayersLoaded}
            />
          )}
          {entity2Id && (
            <EntitySection
              entityId={entity2Id}
              label={entity2Name}
              selectedIds={[...(selectedMap[entity2Id] ?? [])]}
              onToggle={handleToggle}
              onPlayersLoaded={handlePlayersLoaded}
            />
          )}

          {/* <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={loading || totalSelected === 0}
            startIcon={
              loading
                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                : <HowToReg />
            }
            sx={{
              mt: 1,
              bgcolor: ACCENT,
              color: '#fff',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              boxShadow: `0 4px 14px ${ACCENT}55`,
              '&:hover': { bgcolor: '#235f47', boxShadow: `0 6px 18px ${ACCENT}66` },
              '&.Mui-disabled': {
                bgcolor: BORDER,
                color: TEXT_SEC,
                boxShadow: 'none',
              },
            }}
          >
            {loading
              ? 'Inscription en cours…'
              : `Inscrire${totalSelected > 0 ? ` (${totalSelected})` : ''} joueurs`
            }
          </Button> */}
<Button
  variant="contained"
  size="small"
  onClick={handleSubmit}
  disabled={loading || totalSelected === 0 || !isOnline}   // 👈
  startIcon={
    loading
      ? <CircularProgress size={14} sx={{ color: '#fff' }} />
      : <HowToReg />
  }
  sx={{
    mt: 1,
    bgcolor: ACCENT,
    color: '#fff',
    borderRadius: '10px',
    textTransform: 'none',
    fontWeight: 600,
    px: 2.5,
    boxShadow: `0 4px 14px ${ACCENT}55`,
    '&:hover': { bgcolor: '#235f47', boxShadow: `0 6px 18px ${ACCENT}66` },
    '&.Mui-disabled': {
      bgcolor: BORDER,
      color: TEXT_SEC,
      boxShadow: 'none',
    },
  }}
>
  {loading
    ? 'Inscription en cours…'
    : !isOnline
      ? 'Pas de connexion'                                  // 👈
      : `Inscrire${totalSelected > 0 ? ` (${totalSelected})` : ''} joueurs`
  }
</Button>
          
        </>
      )}

      {/* ── Already-registered players ── */}
      <RegisteredTable game={game} />

      {/* ── Success snackbar (green palette) ── */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccess('')}
          sx={{
            bgcolor: ACCENT_BG,
            color: ACCENT,
            border: `1px solid ${ACCENT}55`,
            borderRadius: '10px',
            fontWeight: 600,
            '& .MuiAlert-icon': { color: ACCENT },
            '& .MuiIconButton-root': { color: ACCENT },
          }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlayersTab;